import { useEffect, useMemo, useState, useCallback } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import useSearchForm from 'hooks/useSearchForm';
import {
  getShipmentParams,
  getSearchFormConfigs,
} from 'services/globalConfig.services';
import { usePTPConfigContext } from 'GlobalConfig.context';

interface UseLoginModalProps {
  hasError?: boolean;
  hasNoInfoUpdate?: boolean;
}

export const useLoginModal = ({
  hasError = false,
  hasNoInfoUpdate = false,
}: UseLoginModalProps = {}) => {
  const { t } = useTranslation();
  const configs = usePTPConfigContext();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [customError, setCustomError] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const {
    formFields,
    initSearchForm,
    onChangeSearchForm,
    onSubmitSearchForm,
    onSearchBySearchTerms,
  } = useSearchForm();

  const { searchFormFields, searchFields } = useMemo(
    () => getSearchFormConfigs(configs),
    [configs],
  );

  const tabs = useMemo(() => {
    if (!searchFormFields?.length) return [];

    return searchFormFields
      .map((field) => {
        const found = searchFields.find((item) => item.key === field.key);
        if (!found) return null;
        return { key: found.key, label: t(found.name) };
      })
      .filter((tab) => tab !== null);
  }, [searchFormFields, searchFields, t]);

  const onChangeTab = useCallback((index: number) => {
    setSelectedTabIndex(index);
    setCustomError('');
    setCustomMessage('');
  }, []);

  const handleChangeForm = useCallback(
    (e: Event) => {
      onChangeSearchForm(e);
      setCustomError('');
      setCustomMessage('');
    },
    [onChangeSearchForm],
  );

  const onSubmitForm = useCallback(
    (event: Event) => {
      onSubmitSearchForm(onSearchBySearchTerms)(event);
    },
    [onSubmitSearchForm, onSearchBySearchTerms],
  );

  useEffect(() => {
    if (!tabs.length) return;

    const shipmentParams = getShipmentParams(configs);
    if (!shipmentParams.length) {
      setSelectedTabIndex(0);
      return;
    }

    const key = searchFields.find(
      (f) => f.urlParam === shipmentParams[0].key,
    )?.key;

    const matchedTab = tabs.findIndex((tab) => tab.key === key);
    const tabIndex = matchedTab !== -1 ? matchedTab : 0;

    setSelectedTabIndex(tabIndex);
  }, [tabs, searchFields, configs]);

  useEffect(() => {
    if (!tabs.length || !tabs[selectedTabIndex]) return;

    initSearchForm(tabs[selectedTabIndex].key);
  }, [selectedTabIndex, tabs, initSearchForm]);

  // Handle custom error states
  useEffect(() => {
    if (hasError) {
      setCustomError(
        configs.merchantCustom.noInfoDisplayMessage ||
          t('NO_INFORMATION_MESSAGE_1'),
      );
      setCustomMessage(
        configs.merchantCustom.noInfoDisplayMessage2 ||
          t('NO_INFORMATION_MESSAGE_2'),
      );
    }

    if (hasNoInfoUpdate) {
      setCustomError(
        configs.noInfoUpdate.messageNoUpdate || t('NO_INFO_UPDATE_MESSAGE'),
      );
      // This default message is intended, referenced from RetrySearchForm
      setCustomMessage(
        configs.noInfoUpdate.messageSearchForm || t('NO_INFORMATION_MESSAGE_2'),
      );
    }
  }, [hasError, hasNoInfoUpdate]);

  return {
    // state
    tabs,
    selectedTabIndex,
    shouldShowTabs: tabs.length > 1,
    formFields,
    customError,
    customMessage,

    // handler
    onChangeTab,
    onChangeForm: handleChangeForm,
    onSubmitForm,
  };
};
